import styles from "./tasks-page.module.css";
import { useState, useEffect, useContext } from "react";
import {
  useParams,
  useSearchParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import {
  Typography,
  Flex,
  Button,
  Row,
  Col,
  App,
  Modal,
  Skeleton,
  Card,
  Empty,
  Spin,
  Pagination,
  Segmented,
  Tooltip,
  Dropdown,
  Popover,
  Checkbox,
} from "antd";

import ViewGridIcon from "@/icons/ViewGridIcon";
import TableIcon from "@/icons/TableIcon";
import ViewBoardsIcon from "@/icons/ViewBoardsIcon";
import PlusIcon from "@/icons/PlusIcon";
import FilterIcon from "@/icons/FilterIcon";
import CloseIcon from "@/icons/CloseIcon";
import SortDescendingIcon from "@/icons/SortDescendingIcon";
import SortAscendingIcon from "@/icons/SortAscendingIcon";
import SelectorIcon from "@/icons/SelectorIcon";
import Selector2Icon from "@/icons/Selector2Icon";
import SettingsIcon from "@/icons/SettingsIcon";

import { UserContext } from "@/context/UserContext";

import TaskCard from "@/components/cards/TaskCard";
import TasksTable from "@/components/tables/TasksTable";
import KanbanBoard from "@/components/KanbanBoard";
import TaskExportActions from "@/components/ui/TaskExportActions";
import ShowDeletedTasksSwitch from "@/components/inputs/ShowDeletedTasksSwitch";

import SearchFrom3 from "@/components/inputs/SearchFrom3";
import CustomSelect from "@/components/inputs/CustomSelect";
import CustomTreeSelect from "@/components/inputs/CustomTreeSelect";
import Breadcrumbs from "@/components/ui/Breadcrumbs";

import TaskModal from "@/components/modals/TaskModal";
import CopyTaskModal from "@/components/modals/CopyTaskModal";
import ActualLaborCostsModal from "@/components/modals/ActualLaborCostsModal";
import PlannedLaborCostsModal from "@/components/modals/PlannedLaborCostsModal";
import FiltersModal from "@/components/modals/FiltersModal";
import PublishModal from "@/components/modals/PublishModal";

import { useTaskFilters } from "./hooks/useTaskFilters";
import { useSelectedTaskFilters } from "./hooks/useSelectedTaskFilters";
import { useTableControls } from "./hooks/useTableControls";
import { useKanbanVisibleStatuses } from "./hooks/useKanbanVisibleStatuses";

import { useTableAutoHeight } from "@/hooks/useTableAutoHeight";

import { FiltersModalContent } from "./FiltersModalContent";

import { getList, deleteItem, updateItem } from "@/api/tasks";

import routes from "@/routes";

import {
  SORT_OPTIONS,
  ACCESS_LEVELS,
  FILTER_NAMES,
  VIEW_TYPES,
  TASK_EXPORT_ENABLED,
  getInitialTableParams,
  getLocalStorageKey,
  isTaskStatusFinal,
} from "@/constants/tasks";

const { Title } = Typography;

// Страница задач
// Может менять отображение задач между карточками, таблицей и канбан доской
// Имеет возможность фильтрации и сортировки задач
const Tasks = ({ isAdmins = false }) => {
  const { unit_uuid } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { message } = App.useApp();
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  // Данные
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTaskUuids, setSelectedTaskUuids] = useState([]);
  const [showDeletedTasks, setShowDeletedTasks] = useState(false);

  const initialTableParams = getInitialTableParams(unit_uuid);
  const [tableParams, setTableParams] = useState(initialTableParams);

  // Автоматическая высота таблицы
  const { tableContainerRef, tableHeight, headerHeight } = useTableAutoHeight([
    data,
    tableParams.select_view,
    tableParams.pagination.pageSize,
  ]);

  const [isExpandCards, setIsExpandCards] = useState(false);

  const handleTaskSelect = (uuid, checked) => {
    setSelectedTaskUuids((prev) =>
      checked ? Array.from(new Set([...prev, uuid])) : prev.filter((id) => id !== uuid),
    );
  };

  const visibleTaskUuids = data.map((task) => task.uuid);
  const isAllVisibleCardsSelected =
    !!visibleTaskUuids.length &&
    visibleTaskUuids.every((uuid) => selectedTaskUuids.includes(uuid));
  const isSomeVisibleCardsSelected =
    visibleTaskUuids.some((uuid) => selectedTaskUuids.includes(uuid)) &&
    !isAllVisibleCardsSelected;

  const handleSelectAllVisibleCards = (checked) => {
    setSelectedTaskUuids((prev) =>
      checked
        ? Array.from(new Set([...prev, ...visibleTaskUuids]))
        : prev.filter((uuid) => !visibleTaskUuids.includes(uuid)),
    );
  };

  // Окно создания/редактирования
  const [isModalShow, setIsModalShow] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  // Окно копирования
  const [isCopyModalShow, setIsCopyModalShow] = useState(false);

  // Окно публикации
  const [isPublishModalShow, setIsPublishModalShow] = useState(false);

  // Окно добавления трудозатрат
  const [isActualCostsModalShow, setIsActualCostsModalShow] = useState(false);
  const [isPlannedCostsModalShow, setIsPlannedCostsModalShow] = useState(false);

  // Адаптация вида при загрузке страницы на мобильных устройствах
  useEffect(() => {
    const handleResize = () => {
      if (
        window.innerWidth < 640 &&
        tableParams.select_view !== VIEW_TYPES.CARDS
      ) {
        handleViewChange(VIEW_TYPES.CARDS);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
    // eslint-disable-next-line
  }, [tableParams.select_view]);

  // Запрос на сервер для получения данных
  const fetchData = async (tParams, shouldShowDeleted = showDeletedTasks) => {
    setIsLoading(true);

    const currentParams = tParams || tableParams;
    const params = {
      page:
        currentParams.select_view === VIEW_TYPES.KANBAN
          ? 1
          : currentParams.pagination.current,
      perpage:
        currentParams.select_view === VIEW_TYPES.KANBAN
          ? 1000000000000
          : currentParams.pagination.pageSize,
      sort: currentParams.sort,
      ...currentParams.filters,
      type_filter: currentParams.filters.access_level,
      include_deleted: shouldShowDeleted,
    };

    // Дерево работает с объектами, поэтому для запроса нужно преобразовать их в массив uuids
    if (params.units_uuids?.length) {
      params.units_uuids = params.units_uuids.map(({ value }) => value);
    }

    try {
      const { tasks, total, page, perPage } = await getList(params, isAdmins);
      setData(tasks);

      if (currentParams.select_view === VIEW_TYPES.KANBAN) {
        setParams(currentParams);
      } else {
        const updatedTableParams = {
          ...currentParams,
          pagination: {
            ...currentParams.pagination,
            total,
            current: page,
            pageSize: perPage,
          },
        };
        setParams(updatedTableParams);
      }
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleShowDeletedTasksChange = (checked) => {
    setShowDeletedTasks(checked);
    setSelectedTaskUuids([]);
    fetchData(
      {
        ...tableParams,
        pagination: { ...tableParams.pagination, current: 1 },
      },
      checked,
    );
  };

  // Устанавливаем параметры таблицы и сохраняем их в localStorage
  const setParams = (params) => {
    setTableParams(params);
    const toSave = { ...params, filters: { ...params.filters } };
    delete toSave.filters?.copy_transaction_id;
    delete toSave.filters?.uuid;
    localStorage.setItem(
      getLocalStorageKey(user.user_uuid, unit_uuid, isAdmins),
      JSON.stringify(toSave),
    );
  };

  // Очистка параметра в URL
  const clearQueryParam = (key) => {
    const sp = new URLSearchParams(location.search);
    if (sp.has(key)) {
      sp.delete(key);
      navigate(
        { pathname: location.pathname, search: sp.toString() ? `?${sp}` : "" },
        { replace: true },
      );
    }
  };

  // Хук для работы с фильтрами
  const {
    statuses,
    priorities,
    taskTypes,
    sprints,
    employees,
    units,
    unitsTree,
    unitData,
    optionsPending,
    tempValueFilters,
    isFiltersModalShow,
    setIsFiltersModalShow,
    handleFilterChange,
    handleTempFilterChange,
    openFiltersModal,
    applyFilters,
    clearFilters,
    isAnyFilterApplied,
  } = useTaskFilters({
    unit_uuid,
    isAdmins,
    userUuid: user.user_uuid,
    initialTableParams,
    searchParams,
    location,
    tableParams,
    setParams,
    fetchData,
    clearQueryParam, // чтобы чистить query при сбросе фильтра копирования
  });

  // Хук для управления таблицей
  const { handleViewChange, handleSortChange, handlePaginationChange } =
    useTableControls({
      tableParams,
      setParams,
      fetchData,
      setData,
    });

  // Хук для управления видимыми статусами в канбане
  const { visibleStatuses, handleStatusCheckboxChange } =
    useKanbanVisibleStatuses(statuses, unit_uuid, user.user_uuid, isAdmins);

  // Обработчик добавления. Открывает модальное окно
  const handleAddNew = () => {
    if (tableParams.filters.unit_uuid) {
      setCurrentItem({
        unit_uuid: tableParams.filters.unit_uuid,
      });
    } else {
      setCurrentItem(null);
    }
    setIsModalShow(true);
  };

  // Обработчик копирования. Открывает модальное окно
  const handleCopyItem = (item) => {
    setCurrentItem(item);
    setIsCopyModalShow(true);
  };

  // Обработчик сохранения копии задачи
  const handleSaveCopyItem = (txUuid) => {
    setIsCopyModalShow(false);

    const params = new URLSearchParams({
      copy_transaction_id: txUuid,
    }).toString();
    const allTasksPath = isAdmins ? routes.administration.tasks : routes.tasks;

    navigate({ pathname: allTasksPath, search: `?${params}` });
  };

  // Обработчик редактирования. Открывает модальное окно
  const handleEditItem = (item) => {
    const status =
      statuses.find(({ uuid }) => uuid === item?.status_uuid) || item?.status;

    if (isTaskStatusFinal(status)) return;

    setCurrentItem(item);
    setIsModalShow(true);
  };

  // Обработчик сохранения. Отправляет запрос на сервер и обновляет данные
  const handleSaveItem = () => {
    setIsModalShow(false);
    fetchData();
  };

  // Обработчик удаления
  const handleDeleteItem = (item) => {
    const currentIsDraft = item?.status?.is_for_draft;

    Modal.confirm({
      title: currentIsDraft
        ? `Вы уверены, что хотите удалить черновик ${item.name}?`
        : `Вы уверены, что хотите удалить задачу ${item.name}?`,
      content: currentIsDraft ? (
        <span>
          Черновик будет полностью удален из&nbsp;системы. Это&nbsp;действие
          нельзя будет отменить. Прикрепленные файлы, ресурсы и&nbsp;подзадачи
          останутся в&nbsp;системе.
        </span>
      ) : (
        "Задача будет переведена в статус 'Удалена'"
      ),
      onOk() {
        handleConfirmDelete(item);
      },
      okText: "Удалить",
      cancelText: "Отмена",
    });
  };

  // Действительно удаляем задачу после подтверждения
  const handleConfirmDelete = async (item) => {
    setIsLoading(true);
    try {
      await deleteItem({ uuid: item.uuid });
      await fetchData();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
      setIsLoading(false);
    }
  };

  // Обработчик публикации задачи. Открывает модальное окно
  const handlePublishItem = (item) => {
    setCurrentItem(item);
    setIsPublishModalShow(true);
  };

  // Обработчик закрытия модалки публикации
  const handlePublishSave = () => {
    setIsPublishModalShow(false);
    fetchData();
  };

  // Обработчик добавления трудозатрат
  const handleAddActualCosts = (item) => {
    setCurrentItem(item);
    setIsActualCostsModalShow(true);
  };

  // Обработчик добавления планируемых трудозатрат
  const handleAddPlannedCosts = (item) => {
    setCurrentItem(item);
    setIsPlannedCostsModalShow(true);
  };

  const handleSavePlannedCosts = async (data) => {
    try {
      const item = {
        uuid: currentItem.uuid,
        ...data,
      };
      await updateItem(item);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    }
    fetchData();

    setIsPlannedCostsModalShow(false);
  };

  // После сохранения закрыть модалку
  const onLaborCostSave = () => {
    setIsActualCostsModalShow(false);
    fetchData();
  };

  // Формируем массив выбранных фильтров для отдельной плашки
  const selectedFilters = useSelectedTaskFilters({
    tableParams,
    statuses,
    priorities,
    taskTypes,
    employees,
    sprints,
  });

  // Обработчик снятия выбранного фильтра из плашки
  const handleDeselectFilter = (value) => {
    console.log(value);
    const [name, id] = value.split("__");

    if (name === FILTER_NAMES.STATUS_UUID) {
      handleFilterChange(
        name,
        tableParams.filters.status_uuid.filter((uuid) => uuid !== id),
      );
    } else if (
      name === FILTER_NAMES.COPY_TRANSACTION_ID ||
      name === FILTER_NAMES.TASKS_UUIDS
    ) {
      clearQueryParam(name);
      handleFilterChange(name, null);
    } else {
      handleFilterChange(name, null);
    }
  };

  // Быстрый фильтр "Я исполнитель"
  const isMyExecutorChecked =
    tableParams?.filters?.responsible_executor_uuid === user?.employee_uuid;

  const handleToggleMyExecutor = (checked) => {
    handleFilterChange(
      FILTER_NAMES.RESPONSIBLE_EXECUTOR_UUID,
      checked ? user?.employee_uuid : null,
    );
  };

  return (
    <>
      {optionsPending ? (
        <div className="spin">
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Flex vertical gap={25} className="h-100">
            {/* Хлебные крошки и заголовок */}
            <Flex vertical gap={10}>
              <Breadcrumbs
                items={
                  isAdmins
                    ? [
                        { title: "Административная панель" },
                        {
                          title: "Задачи",
                        },
                        {
                          title: "Список задач",
                        },
                      ]
                    : [
                        {
                          title: "Главная",
                          href: routes.dashboards,
                        },

                        unitData?.name
                          ? {
                              title: unitData.name,
                            }
                          : {},
                        {
                          title: "Задачи",
                        },
                        {
                          title: "Список задач",
                        },
                      ]
                }
              />

              <Flex align="center" justify="space-between" gap={20}>
                <Title level={2} className="no-margin">
                  Задачи
                </Title>

                <Flex align="center" gap={8}>
                  <TaskExportActions
                    selectedTasks={selectedTaskUuids}
                    onClear={() => setSelectedTaskUuids([])}
                    isAdmins={isAdmins}
                  />

                  {/* Показываем кнопку Добавить для всех видов, кроме канбан, а для канбан - настройки */}
                  {tableParams.select_view !== VIEW_TYPES.KANBAN ? (
                    <Button
                      type="primary"
                      icon={<PlusIcon />}
                      onClick={handleAddNew}
                    />
                  ) : (
                    <Popover
                      trigger="click"
                      placement="bottomRight"
                      arrow={false}
                      content={
                        <Flex vertical gap={5}>
                          {statuses.map((status) => (
                            <Checkbox
                              key={status.uuid}
                              checked={visibleStatuses.includes(status.uuid)}
                              onChange={(e) =>
                                handleStatusCheckboxChange(
                                  status.uuid,
                                  e.target.checked,
                                )
                              }
                            >
                              {status.label ?? status.title ?? status.key}
                            </Checkbox>
                          ))}
                        </Flex>
                      }
                    >
                      <Button
                        color="default"
                        variant="outlined"
                        icon={<SettingsIcon />}
                      >
                        Настройки
                      </Button>
                    </Popover>
                  )}
                </Flex>
              </Flex>
            </Flex>

            {/* Поиск и фильтры */}
            <Flex vertical gap={8} className={styles.filtersWrap}>
              <Flex gap={25} align="start" justify="space-between">
                <Flex gap={8} align="center" className={styles.search}>
                  <SearchFrom3
                    placeholder="Поиск"
                    defaultValue={tableParams.filters.search}
                    onSearch={(search) =>
                      handleFilterChange(FILTER_NAMES.SEARCH, search)
                    }
                  />

                  <Flex gap={8} className={styles.filters}>
                    {isAdmins ? (
                      <CustomSelect
                        placeholder="Рабочая группа"
                        options={units}
                        fieldNames={{
                          label: "name",
                          value: "uuid",
                          key: "uuid",
                        }}
                        style={{
                          width: 150,
                        }}
                        showSearch
                        optionFilterProp="name"
                        allowClear
                        popupMatchSelectWidth={false}
                        value={tableParams.filters.unit_uuid}
                        onChange={(unit) =>
                          handleFilterChange(FILTER_NAMES.UNIT_UUID, unit)
                        }
                      />
                    ) : (
                      <>
                        <CustomTreeSelect
                          placeholder="Рабочая группа"
                          data={unitsTree}
                          value={tableParams.filters.units_uuids}
                          onChange={(units_uuids) => {
                            handleFilterChange(
                              FILTER_NAMES.UNITS_UUIDS,
                              units_uuids,
                            );
                          }}
                          style={{
                            width: "180px",
                          }}
                        />
                        <CustomSelect
                          placeholder="Тип"
                          options={ACCESS_LEVELS}
                          style={{
                            width: 135,
                          }}
                          popupMatchSelectWidth={false}
                          value={tableParams.filters.access_level}
                          onChange={(val) =>
                            handleFilterChange(FILTER_NAMES.ACCESS_LEVEL, val)
                          }
                          optionRender={(option) => (
                            <Flex gap={5} align="center">
                              {option?.data?.icon}
                              <span>{option.label}</span>
                            </Flex>
                          )}
                          labelRender={(option) => {
                            const item = ACCESS_LEVELS.find(
                              (item) => item.value === option.value,
                            );

                            return (
                              <Flex gap={5} align="center">
                                {item?.icon}
                                <span>{item.label}</span>
                              </Flex>
                            );
                          }}
                        />
                      </>
                    )}

                    {tableParams.select_view === VIEW_TYPES.KANBAN && (
                      <Checkbox
                        style={{
                          alignItems: "center",
                          border: "1px solid #D4D4D4",
                          borderRadius: 4,
                          padding: "4px 2px 2px 10px",
                          background: "#fff",
                        }}
                        checked={isMyExecutorChecked}
                        onChange={(e) =>
                          handleToggleMyExecutor(e.target.checked)
                        }
                      >
                        Я&nbsp;исполнитель
                      </Checkbox>
                    )}
                  </Flex>

                  <Button
                    type="primary"
                    icon={<FilterIcon />}
                    onClick={openFiltersModal}
                  ></Button>

                  {isAnyFilterApplied && (
                    <Tooltip
                      title="Очистить все фильтры"
                      placement="bottom"
                      className={styles.cleanFilters}
                    >
                      <Button
                        color="default"
                        variant="link"
                        shape="circle"
                        size="small"
                        icon={<CloseIcon />}
                        onClick={clearFilters}
                        style={{ background: "#BBBBBB", color: "#fff" }}
                      ></Button>
                    </Tooltip>
                  )}
                </Flex>

                <Flex gap={8} align="center">
                  <CustomSelect
                    placeholder="Сортировка"
                    options={SORT_OPTIONS}
                    style={{
                      width: 165,
                    }}
                    popupMatchSelectWidth={false}
                    value={tableParams.sort}
                    onChange={handleSortChange}
                    className={styles.bigSorting}
                  />

                  <div className={styles.smallSorting}>
                    <Dropdown
                      menu={{
                        items: SORT_OPTIONS,
                        selectable: true,
                        selectedKeys: tableParams.sort,
                        onClick: (e) => {
                          handleSortChange(e.key);
                        },
                      }}
                      trigger={["click"]}
                    >
                      <Button
                        icon={
                          tableParams.sort?.startsWith("-") ? (
                            <SortDescendingIcon style={{ fontSize: 16 }} />
                          ) : (
                            <SortAscendingIcon style={{ fontSize: 16 }} />
                          )
                        }
                      />
                    </Dropdown>
                  </div>

                  <Segmented
                    value={tableParams.select_view}
                    options={[
                      {
                        value: VIEW_TYPES.LIST,
                        icon: <TableIcon />,
                      },
                      {
                        value: VIEW_TYPES.CARDS,
                        icon: <ViewGridIcon />,
                      },
                      {
                        value: VIEW_TYPES.KANBAN,
                        icon: <ViewBoardsIcon />,
                      },
                    ]}
                    onChange={handleViewChange}
                    className={styles.view}
                  />

                  <Button
                    icon={
                      isExpandCards ? (
                        <Selector2Icon style={{ fontSize: 16 }} />
                      ) : (
                        <SelectorIcon style={{ fontSize: 16 }} />
                      )
                    }
                    onClick={() => setIsExpandCards(!isExpandCards)}
                    className={styles.viewMobile}
                  />
                </Flex>
              </Flex>

              {!!selectedFilters.length && (
                <CustomSelect
                  className={`${styles.selectedFilters} select__not-active`}
                  mode="multiple"
                  value={selectedFilters.map((f) => f.value)}
                  options={selectedFilters}
                  maxTagCount="responsive"
                  popupRender={() => null}
                  open={false}
                  suffixIcon={false}
                  showSearch={false}
                  onDeselect={handleDeselectFilter}
                  style={{ width: "100%" }}
                />
              )}

            </Flex>

            {/* Контент */}
            <Flex vertical gap={20} style={{ flex: 1, minHeight: 0 }}>
              <ShowDeletedTasksSwitch
                checked={showDeletedTasks}
                onChange={handleShowDeletedTasksChange}
                disabled={isLoading}
              />

              {TASK_EXPORT_ENABLED &&
                tableParams.select_view === VIEW_TYPES.CARDS && (
                <Checkbox
                  checked={isAllVisibleCardsSelected}
                  indeterminate={isSomeVisibleCardsSelected}
                  onChange={(e) =>
                    handleSelectAllVisibleCards(e.target.checked)
                  }
                  style={{ marginTop: 8 }}
                >
                  Выделить все
                </Checkbox>
              )}

              {tableParams.select_view === VIEW_TYPES.CARDS &&
                (isLoading ? (
                  <Row gutter={[16, 16]}>
                    {Array.from({ length: 6 }).map((_, index) => (
                      <Col xs={24} sm={24} md={12} lg={8} xl={8} key={index}>
                        <Card>
                          <Skeleton active />
                        </Card>
                      </Col>
                    ))}
                  </Row>
                ) : data?.length ? (
                  <>
                    <Row
                      gutter={[16, 16]}
                      style={{
                        overflow: "auto",
                        flex: 1,
                        paddingBlock: 10,
                      }}
                    >
                      {data.map((task) => (
                        <Col
                          xs={24}
                          sm={12}
                          md={8}
                          lg={8}
                          xl={8}
                          xxl={6}
                          key={task.uuid}
                        >
                          <TaskCard
                            task={task}
                            statuses={statuses}
                            priorities={priorities}
                            taskTypes={taskTypes}
                            onEdit={handleEditItem}
                            onDelete={handleDeleteItem}
                            onCopyItem={handleCopyItem}
                            onPublishItem={handlePublishItem}
                            isAdmins={isAdmins}
                            unit={unit_uuid}
                            isExpandCards={isExpandCards}
                            selectable={
                              TASK_EXPORT_ENABLED && !!selectedTaskUuids.length
                            }
                            selected={selectedTaskUuids.includes(task.uuid)}
                            onSelect={
                              TASK_EXPORT_ENABLED ? handleTaskSelect : undefined
                            }
                          />
                        </Col>
                      ))}
                    </Row>

                    <div style={{ minHeight: 32 }}>
                      <Pagination
                        {...tableParams.pagination}
                        responsive={true}
                        onChange={handlePaginationChange}
                      />
                    </div>
                  </>
                ) : (
                  <Card>
                    <Empty />
                  </Card>
                ))}
              {tableParams.select_view === VIEW_TYPES.LIST && (
                <>
                  <div
                    ref={tableContainerRef}
                    style={{ flex: 1, minHeight: 0 }}
                  >
                    <TasksTable
                      data={data}
                      loading={isLoading}
                      statuses={statuses}
                      priorities={priorities}
                      taskTypes={taskTypes}
                      pagination={false}
                      onCopyItem={handleCopyItem}
                      onEditItem={handleEditItem}
                      onDeleteItem={handleDeleteItem}
                      onPublishItem={handlePublishItem}
                      onAddActualCosts={handleAddActualCosts}
                      onEditPlannedCosts={handleAddPlannedCosts}
                      unit={unit_uuid}
                      isAdmins={isAdmins}
                      resize
                      scroll={{ x: "100%", y: tableHeight - headerHeight }}
                      selectedRowKeys={
                        TASK_EXPORT_ENABLED ? selectedTaskUuids : undefined
                      }
                      onRowsSelect={
                        TASK_EXPORT_ENABLED ? setSelectedTaskUuids : undefined
                      }
                    />
                  </div>

                  <div style={{ minHeight: 32 }}>
                    <Pagination
                      {...tableParams.pagination}
                      onChange={handlePaginationChange}
                    />
                  </div>
                </>
              )}
              {tableParams.select_view === VIEW_TYPES.KANBAN && (
                <KanbanBoard
                  data={data}
                  loading={isLoading}
                  statuses={statuses}
                  priorities={priorities}
                  taskTypes={taskTypes}
                  unit={unit_uuid}
                  isAdmins={isAdmins}
                  visibleStatuses={visibleStatuses}
                  onAddNew={handleAddNew}
                  selectable={TASK_EXPORT_ENABLED}
                  selectedTaskUuids={
                    TASK_EXPORT_ENABLED ? selectedTaskUuids : []
                  }
                  onSelectTask={
                    TASK_EXPORT_ENABLED ? handleTaskSelect : undefined
                  }
                />
              )}
            </Flex>
          </Flex>

          {isModalShow && (
            <TaskModal
              isAdmins={isAdmins}
              data={currentItem}
              statuses={statuses}
              priorities={priorities}
              taskTypes={taskTypes}
              units={units}
              visible={isModalShow}
              onSave={handleSaveItem}
              onCancel={() => setIsModalShow(false)}
              unit_uuid={unit_uuid}
            />
          )}

          {isCopyModalShow && (
            <CopyTaskModal
              visible={isCopyModalShow}
              onCancel={() => setIsCopyModalShow(false)}
              onSave={handleSaveCopyItem}
              task={currentItem}
              units={units}
              isAdmins={isAdmins}
              unit_uuid={unit_uuid}
            />
          )}

          {isFiltersModalShow && (
            <FiltersModal
              visible={isFiltersModalShow}
              onSave={applyFilters}
              onCancel={() => {
                setIsFiltersModalShow(false);
              }}
              onClear={() => {
                setIsFiltersModalShow(false);
                clearFilters();
              }}
            >
              <FiltersModalContent
                isAdmins={isAdmins}
                tempValueFilters={tempValueFilters}
                units={units}
                unitsTree={unitsTree}
                statuses={statuses}
                priorities={priorities}
                taskTypes={taskTypes}
                employees={employees}
                sprints={sprints}
                user={user}
                handleTempFilterChange={handleTempFilterChange}
              />
            </FiltersModal>
          )}

          {isPublishModalShow && (
            <PublishModal
              data={currentItem}
              statuses={statuses}
              visible={isPublishModalShow}
              onSave={handlePublishSave}
              onCancel={() => setIsPublishModalShow(false)}
            />
          )}

          {isActualCostsModalShow && (
            <ActualLaborCostsModal
              data={null}
              task_uuid={currentItem?.uuid}
              unit_uuid={currentItem?.unit_uuid}
              visible={isActualCostsModalShow}
              onCancel={() => setIsActualCostsModalShow(false)}
              onSave={onLaborCostSave}
              isAdmins={isAdmins}
            />
          )}

          {isPlannedCostsModalShow && (
            <PlannedLaborCostsModal
              data={currentItem}
              visible={isPlannedCostsModalShow}
              parentDoNotExceedChildren={
                currentItem?.parent?.do_not_exceed_children || false
              }
              plannedUpLimit={
                currentItem?.uuid
                  ? (currentItem?.parent?.available_limit_for_children ??
                      null) + currentItem?.planned_costs
                  : (currentItem?.parent?.available_limit_for_children ?? null)
              }
              plannedLowLimit={currentItem?.sum_children_planned_costs}
              onCancel={() => setIsPlannedCostsModalShow(false)}
              onSave={handleSavePlannedCosts}
            />
          )}
        </>
      )}
    </>
  );
};

export default Tasks;
